/**
 * shopify-forms-grid.js
 *
 * Per-field column widths and field hiding for the Shopify Forms B2B
 * application form.
 *
 * The form renders inside an OPEN shadow root on <shopify-forms-embed>, out of
 * reach of theme CSS, so a constructable stylesheet is appended to the shadow
 * root's adoptedStyleSheets instead. Appending at the END means equal-specificity
 * rules win over the component's own.
 *
 * Scoping: only shadow roots whose form contains #company_name are touched — the
 * B2B application form is the only Forms instance with it. Popup/newsletter
 * forms are left alone.
 *
 * Selectors are prefix-matched on the stable CSS-module prefixes
 * (_formFieldset_, _multiFieldLine_, _formPhoneInputContainer_) — never on the
 * full hashed class, which changes between app deploys. Field hooks are the
 * stable input ids. Two ids contain a literal '#' (custom#web_instagram,
 * custom#professional_sector selector value), so those use the [id="..."]
 * attribute form: an escaped id selector would be brittle.
 *
 * Deleted app fields: #phone_number (top-level phone) was removed from the
 * form in the Shopify Forms app on 2026-08-04. Its span rule is kept below as
 * an inert no-op in case it returns; --lv-span-phone currently matches
 * nothing. custom#referral was deleted the same day but has since been
 * restored — --lv-span-referral is live again.
 *
 * Hidden rows: Shopify Forms renders "Company billing address" as one
 * composite field with no per-subfield control in the app, so unwanted rows
 * are hidden here. display:none removes the grid item (no empty track) but
 * keeps it mounted and in the app's state, so hidden fields submit as empty
 * values rather than being omitted. Why each row is hidden:
 *   - billing_address_recipient   "Empresa / Atención" duplicates the Empresa
 *                                 field (company_name)
 *   - billing_address_country     single-market form; the mounted default
 *                                 (España) keeps driving the address
 *                                 localization that fills the Provincia
 *                                 (billing_address_zone) options. The row has
 *                                 no element with id billing_address_country;
 *                                 the stable handle is the select toggle's
 *                                 #billing_address_country-selector-value.
 *                                 Exact-id match only: the phone row holds the
 *                                 dial-code picker #country-selector-value,
 *                                 which must NOT be caught.
 *   - billing_address_first_name / _last_name
 *                                 duplicate Nombre/Apellidos. They share one
 *                                 row and one tunable: hiding one without the
 *                                 other makes no sense.
 *   - billing_address_address2    "Apartamento, local..." — dropped from the
 *                                 reduced address block.
 * NEVER hide billing_address_phone. With #phone_number deleted it is the only
 * phone on the form, and server-side validation rejects submissions without
 * it (verified — see docs/FORMS_B2B.md).
 *
 * Every width and hide is a CSS custom property tunable from outside (set
 * them on :root or the section wrapper; they inherit into the shadow tree):
 *   --lv-form-col-gap / --lv-form-row-gap        gaps (default 12px)
 *   --lv-span-first-name / last-name             default 6 / 6
 *   --lv-span-email                              default 12
 *   --lv-span-phone                              inert (#phone_number deleted)
 *   --lv-span-company                            default 6
 *   --lv-span-address1                           default 12
 *   --lv-span-address2                           default 4 (row hidden below)
 *   --lv-span-tax-id                             default 6
 *   --lv-span-referral                           default 6
 *   --lv-span-web / sector                       default 6 / 6
 *   --lv-show-recipient                          default none; set to block
 *                                                to restore Empresa / Atención
 *   --lv-show-country                            default none; set to block
 *                                                to restore País
 *   --lv-show-billing-name                       default none; set to block
 *                                                to restore Nombre/Apellido
 *                                                (one row, restored together)
 *   --lv-show-address2                           default none; set to block
 *                                                to restore Apartamento, local
 *
 * Header typography, scoped to the _formHeader_ section ONLY so the
 * "Dirección fiscal" legend inside the form body (same _textBody_ class) is
 * untouched.
 *   --lv-heading-font / size / weight / line-height   H2 "Crea tu cuenta"
 *   --lv-body-font / size / weight / line-height      header subparagraph
 * The fallbacks are NOT inherit: an adopted-sheet inherit pierces the shadow
 * boundary and pulls the PAGE's font (PP Mori 14px) into the header, because
 * the app sets its type via `font: var(--forms-heading-font)` shorthands, not
 * via inheritance (verified 2026-08-04). Instead each fallback mirrors the
 * app's own cascade — its --forms-heading-font-size / --forms-base-font-size
 * vars where they exist, its literal values (Arial, 400, 26px/20px) where it
 * hardcodes them — so nothing changes until a tunable is set. If a Forms
 * release restyles the header, re-mirror these fallbacks.
 *
 * Theme fonts reach the shadow root through these, defined on :root in
 * snippets/css-variables.liquid (fonts are already loaded by the document,
 * so nothing extra loads):
 *   --font-heading-family   theme heading font (currently 'PP Mori')
 *   --font-body-family      theme body font (currently 'PP Mori')
 *   --font-pp-hatton-family brand display font 'PP Hatton'
 * e.g. --lv-heading-font: var(--font-heading-family);
 *
 * CTA corner radius needs no rule here: the app itself maps
 * --button-border-radius (set it on the embed from Custom CSS) onto its
 * internal --buttons-radius.
 *
 * Failure policy: any error leaves the form untouched and stacked. Feature
 * gates bail silently on browsers without adoptedStyleSheets or :has().
 */
(function () {
  'use strict';

  try {
    // ---- feature gates -----------------------------------------------------
    if (!('adoptedStyleSheets' in Document.prototype)) return;
    if (!(window.CSS && CSS.supports && CSS.supports('selector(:has(*))'))) return;

    var CSS_TEXT = [
      'form[class*="_formFieldset_"] {',
      '  display: grid;',
      '  grid-template-columns: repeat(12, minmax(0, 1fr));',
      '  column-gap: var(--lv-form-col-gap, 12px);',
      '  row-gap: var(--lv-form-row-gap, 12px);',
      '  align-items: start;',
      '}',
      'form[class*="_formFieldset_"] > * { grid-column: span 12; min-width: 0; }',

      'form[class*="_formFieldset_"] > :has(#first_name) { grid-column: span var(--lv-span-first-name, 6); }',
      'form[class*="_formFieldset_"] > :has(#last_name) { grid-column: span var(--lv-span-last-name, 6); }',
      'form[class*="_formFieldset_"] > :has(#email) { grid-column: span var(--lv-span-email, 12); }',
      'form[class*="_formFieldset_"] > :has(#phone_number) { grid-column: span var(--lv-span-phone, 4); }',
      'form[class*="_formFieldset_"] > :has(#company_name) { grid-column: span var(--lv-span-company, 6); }',
      'form[class*="_formFieldset_"] > :has(#billing_address_address1) { grid-column: span var(--lv-span-address1, 12); }',
      'form[class*="_formFieldset_"] > :has(#billing_address_address2) { grid-column: span var(--lv-span-address2, 4); }',
      'form[class*="_formFieldset_"] > :has(#company_tax_id) { grid-column: span var(--lv-span-tax-id, 6); }',
      'form[class*="_formFieldset_"] > :has([id="custom#referral-selector-value"]) { grid-column: span var(--lv-span-referral, 6); }',
      'form[class*="_formFieldset_"] > :has([id="custom#web_instagram"]) { grid-column: span var(--lv-span-web, 6); }',
      'form[class*="_formFieldset_"] > :has([id="custom#professional_sector-selector-value"]) { grid-column: span var(--lv-span-sector, 6); }',

      // Hidden rows (rationale in the header). Tunable: set the property to
      // block from outside to restore a row without editing this file.
      // display:none removes the grid item, so no empty track is left behind.
      // billing_address_phone is deliberately NOT here — hiding it makes
      // server-side validation reject the submission.
      'form[class*="_formFieldset_"] > :has(#billing_address_recipient) { display: var(--lv-show-recipient, none); }',
      'form[class*="_formFieldset_"] > :has(#billing_address_country-selector-value) { display: var(--lv-show-country, none); }',
      'form[class*="_formFieldset_"] > :has(#billing_address_first_name) { display: var(--lv-show-billing-name, none); }',
      'form[class*="_formFieldset_"] > :has(#billing_address_last_name) { display: var(--lv-show-billing-name, none); }',
      'form[class*="_formFieldset_"] > :has(#billing_address_address2) { display: var(--lv-show-address2, none); }',

      // These containers declare width: -webkit-fill-available, which is
      // unreliable on a grid item. Pin them to the tracks they span.
      'form[class*="_formFieldset_"] > [class*="_multiFieldLine_"],',
      'form[class*="_formFieldset_"] > [class*="_formPhoneInputContainer_"] { width: 100%; }',

      // Header typography (see header comment). Scoped to the _formHeader_
      // section so the form-body "Dirección fiscal" legend, which shares the
      // _textBody_ class, is not matched. Fallbacks mirror the app's own
      // values (NOT inherit — that would pull the page font through the
      // shadow boundary), so these are inert until a tunable is set.
      'section[class*="_formHeader_"] h2[class*="_textHeading_"] {',
      '  font-family: var(--lv-heading-font, Arial, sans-serif);',
      '  font-size: var(--lv-heading-size, var(--forms-heading-font-size, 22px));',
      '  font-weight: var(--lv-heading-weight, 400);',
      '  line-height: var(--lv-heading-line-height, 26px);',
      '}',
      'section[class*="_formHeader_"] [class*="_textBody_"] {',
      '  font-family: var(--lv-body-font, Arial, sans-serif);',
      '  font-size: var(--lv-body-size, var(--forms-base-font-size, 14px));',
      '  font-weight: var(--lv-body-weight, 400);',
      '  line-height: var(--lv-body-line-height, 20px);',
      '}',

      '@media (max-width: 749px) {',
      '  form[class*="_formFieldset_"] > * { grid-column: span 12 !important; }',
      '}'
    ].join('\n');

    // Built once; constructable sheets are shareable across shadow roots.
    var sheet = new CSSStyleSheet();
    sheet.replaceSync(CSS_TEXT);

    var applied = new WeakSet(); // shadow roots already carrying the sheet
    var watched = new WeakSet(); // shadow roots with their own observer

    function applyTo(shadowRoot) {
      try {
        if (applied.has(shadowRoot)) return;
        // GUARD: B2B application form only.
        if (!shadowRoot.querySelector('#company_name')) return;
        shadowRoot.adoptedStyleSheets = shadowRoot.adoptedStyleSheets.concat(sheet);
        applied.add(shadowRoot);
      } catch (e) {
        /* leave the form stacked */
      }
    }

    function watch(host) {
      try {
        var sr = host.shadowRoot;
        if (!sr) return; // closed or not yet attached; the doc observer retries
        applyTo(sr);
        if (watched.has(sr)) return;
        watched.add(sr);
        // The component swaps content between the form and success steps;
        // re-check whenever the shadow tree changes.
        new MutationObserver(function () {
          applyTo(sr);
        }).observe(sr, { childList: true, subtree: true });
      } catch (e) {
        /* leave the form stacked */
      }
    }

    function scan() {
      try {
        var hosts = document.querySelectorAll('shopify-forms-embed');
        for (var i = 0; i < hosts.length; i++) watch(hosts[i]);
      } catch (e) {
        /* leave the form stacked */
      }
    }

    // Immediate pass + document-wide observer (the embed mounts lazily, after
    // consent, and can be re-created). Never disconnected.
    scan();
    new MutationObserver(scan).observe(document.documentElement, {
      childList: true,
      subtree: true
    });
    // Theme editor re-renders sections without a page load.
    document.addEventListener('shopify:section:load', scan);
  } catch (e) {
    /* feature-detect or setup failed: form stays stacked and functional */
  }
})();
